import type { BaseLanguageType, LoginData, ThemeType } from '@shared';

import { defaultTheme } from '@asset/themes/config';
import { LANGUAGE, THEME, USER_DATA } from '@common';
import * as Sentry from '@sentry/react';
import { I18nConfig } from '@util/i18n';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'src/hooks';
import { createContainer } from 'unstated-next';

function useUser() {
  const [persistUserData, setPersistUserData] =
    useLocalStorage<LoginData | null>(USER_DATA, null);
  const [persistTheme, setPersistTheme] = useLocalStorage<ThemeType>(
    THEME,
    defaultTheme
  );
  const [persistLanguage, setPersistLanguage] =
    useLocalStorage<BaseLanguageType>(
      LANGUAGE,
      I18nConfig.lng as BaseLanguageType
    );

  const [userData, setUserData] = useState<LoginData | null>(persistUserData);
  const [theme, _setTheme] = useState<ThemeType>(persistTheme);
  const [language, _setLanguage] = useState(persistLanguage);

  const [isLogin, setIsLogin] = useState<boolean | null>(null);
  const [isNoMetamask, setIsNoMetamask] = useState(false);
  const { i18n } = useTranslation();

  const setTheme = (type: ThemeType) => {
    _setTheme(type);
    setPersistTheme(type);
  };

  const setLanguage = (lang: BaseLanguageType) => {
    _setLanguage(lang);
    setPersistLanguage(lang);
    i18n.changeLanguage(lang);
    dayjs.locale(lang?.toLocaleLowerCase());
  };

  const setUser = (data: LoginData | null) => {
    setPersistUserData(data);
    setUserData(data);
  };

  const getMetamaskStatus = (_userData?: LoginData) => {
    // Check the default wallet is auto generated or not
    const finalUserData = _userData || userData;
    if (
      finalUserData?.user?.wallet?.find(
        (w) =>
          w.wallet_address === finalUserData.user.wallet?.[0].wallet_address
      )?.is_treasury
    )
      return true;

    return false;
  };

  const updateMetamaskStatus = (_userData: LoginData) => {
    setIsNoMetamask(getMetamaskStatus(_userData));
  };

  useEffect(() => {
    try {
      if (userData) {
        setIsLogin(true);
        updateMetamaskStatus(userData);
      } else setIsLogin(false);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, []);

  return {
    isLogin,
    isNoMetamask,
    language,
    setIsLogin,
    setLanguage,
    setTheme,
    setUser,
    theme,
    updateMetamaskStatus,
    userData,
  };
}

export const user = createContainer(useUser);
